<template>
	<VSheet
		outlined
		color="am-blue-lighten-90"
		class="rounded-lg"
	>
		<VCard
			class="rounded-lg"
			:class="$isMobile() ? 'pa-4' : 'pa-6'"
			outlined
			color="am-blue-lighten-97"
		>
			<FormStep
				v-if="alert"
				:title="t('title')"
				:question="lastQuestion"
				:description="alertContent"
				variant="alert"
			/>
			<StartStep
				v-else-if="current === 'start'"
				@next="current = 'local'"
			/>
			<FormStep
				v-else-if="current === 'local'"
				:key="index"
				:title="t('title')"
				:question="localQuestions[index].title"
				:placeholder="localQuestions[index].placeholder"
				:variant="localQuestions[index].type"
				:description="localQuestions[index].description"
				:items="localQuestions[index].values"
				:current="Math.round(50 / localQuestions.length * index)"
				:loading="state === StateEnum.PENDING"
				@next="next"
			/>
			<FormStep
				v-else-if="current === 'api'"
				:key="index"
				:title="t('title')"
				:question="apiQuestions[index].title"
				:placeholder="apiQuestions[index].placeholder"
				:variant="apiQuestions[index].type"
				:items="formatItems(apiQuestions[index].values)"
				:current="Math.round(50 / apiQuestions.length * index + 50)"
				:loading="state === StateEnum.PENDING"
				@next="next"
			/>
			<EndStep
				v-else-if="current === 'end'"
				@next="$router.push('/register')"
			/>
		</VCard>
	</VSheet>
</template>

<script lang="ts">
	import Vue from 'vue';
	import Component from 'vue-class-component';
	import { StateEnum } from '@cnamts/vue-dot/src/constants/enums/StateEnum';
	import { mapActions, mapState } from 'vuex';

	import StartStep from '@/components/SimulatorSteps/StartStep';
	import EndStep from '@/components/SimulatorSteps/EndStep/EndStep.vue';

	import {
		RuleOptions,
		QuestionsOptions,
		QuestionData,
		Question
	} from '@/services/ei/eligibility/types';
	import { getRules, getQuestions } from '@/services/ei/eligibility/api';
	import FormStep from '@/components/FormStep/FormStep.vue';
	import { SimulatorItem, SimulatorQuestion } from '@/types';
	import getSimulatorQuestions from '@/functions/getSimulatorQuestions';

	@Component<SimulatorSteps>({
		components: {
			FormStep,
			EndStep,
			StartStep
		},
		methods: {
			...mapActions('notification', ['addNotification']),
			...mapActions('appState', ['updateUser']),
			...mapActions('registerState', ['updateDocumentInfo']),
			...mapActions('registerState', ['updateAnswers'])
		},
		computed: {
			...mapState('appState', ['user'])
		},
		watch: {
			'$i18n.locale'(): void {
				this.localQuestions = getSimulatorQuestions(this);
			}
		}
	})
	export default class SimulatorSteps extends Vue {
		StateEnum = StateEnum;
		state = StateEnum.IDLE;
		alert = false;
		alertContent = '';
		lastQuestion = '';
		current = 'start';
		index = 0;
		localQuestions: SimulatorQuestion[] = [];
		localAnswers: Question[] = [];
		apiQuestions: QuestionData[] = [];
		apiAnswers: Question[] = [];

		mounted() {
			this.localQuestions = getSimulatorQuestions(this);
		}
		formatItems(items: Record<string, string>, desc?: Record<string, string>): SimulatorItem[] {
			const result: SimulatorItem[] = [];
			for (const key in items) {
				result.push({
					text: items[key],
					value: key,
					description: desc ? desc[key] : undefined
				});
			}
			return result;
		}
		async getQuestions(): Promise<void> {
			if (!this.user.nationality || !this.user.birthdate) {
				this.addNotification({
					type: 'error',
					message: this.t('error')
				});
				return;
			}
			this.state = StateEnum.PENDING;
			const params: QuestionsOptions = {
				nationality: this.user.nationality,
				birthdate: this.user.birthdate
			};
			try {
				const { data } = await getQuestions(params);
				this.apiQuestions = data;
				this.state = StateEnum.RESOLVED;
			} catch {
				this.state = StateEnum.REJECTED;
			}
			if (this.apiQuestions.length > 0) {
				this.index = 0;
				this.current = 'api';
				return;
			}
			this.updateAnswers([]);
			await this.send();
		}
		updateUserWithAnswers(answers: Question[]): void {
			answers.forEach((answer) => {
				if (answer.questionKey === 'nationality') {
					this.updateUser({ nationality: answer.answer });
				} else if (answer.questionKey === 'birthdate') {
					this.updateUser({ birthdate: answer.answer });
				}
			});
		}
		next(value: string): void {
			let questions: QuestionData[] | SimulatorQuestion[];
			let answers: Question[];
			if (this.current === 'local') {
				questions = this.localQuestions;
				answers = this.localAnswers;
			} else {
				questions = this.apiQuestions;
				answers = this.apiAnswers;
			}
			answers.push({
				questionKey: questions[this.index].key,
				answer: value
			});
			let i = 1;
			while (!this.checkCondition(questions[this.index + i])) {
				if (this.index + i >= questions.length) {
					if (this.current === 'local') {
						this.updateUserWithAnswers(answers);
						this.getQuestions();
					} else {
						this.updateAnswers(answers);
						this.send();
					}
					return;
				}
				i++;
			}
			this.lastQuestion = questions[this.index].title;
			this.index += i;
		}
		async send(): Promise<void> {
			if (!this.user.nationality || !this.user.birthdate) {
				this.addNotification({
					type: 'error',
					message: this.t('error')
				});
				return;
			}
			try {
				this.state = StateEnum.PENDING;
				const params: RuleOptions = {
					nationality: this.user.nationality,
					birthdate: this.user.birthdate,
					answers: this.apiAnswers
				};
				const { data } = await getRules(params);
				this.state = StateEnum.RESOLVED;
				if (data.eligible) {
					this.updateUser({ rules: data });
					this.updateDocumentInfo({
						requiredAttachments: data.requiredAttachments.map((attachment) => ({
							id: attachment,
							title: this.$t('views.register.document.attachments.' + attachment).toString(),
							state: 'initial'
						})),
						optionalAttachments: data.optionalAttachments
							.filter((attachment) => attachment !== 'BANK_DETAILS' && attachment !== 'BANK_DETAILS_DELEGATE')
							.map((attachment) => ({
								id: attachment,
								title: this.$t('views.register.document.attachments.' + attachment).toString(),
								state: 'initial'
							})),
						bankAttachments: data.optionalAttachments
							.filter((attachment) => attachment === 'BANK_DETAILS' || attachment === 'BANK_DETAILS_DELEGATE')
							.map((attachment) => ({
								id: attachment,
								title: this.$t('views.register.document.attachments.' + attachment).toString(),
								state: 'initial'
							}))
					});
					this.current = 'end';
				} else {
					this.alert = true;
					this.alertContent = data.nonEligibilityReason;
				}
			} catch {
				this.state = StateEnum.REJECTED;
			}
		}
		checkCondition(question: QuestionData | SimulatorQuestion): boolean {
			if (!question) {
				return false;
			}
			const condition = question.condition;
			if (!condition) {
				return true;
			}
			let answers: Question[];
			if (this.current === 'local') {
				answers = this.localAnswers;
			} else {
				answers = this.apiAnswers;
			}
			const answer = answers.find((a) => a.questionKey === condition.key);
			if (!answer) {
				return false;
			}
			if (condition.operator === 'eq') {
				return answer.answer === condition.value;
			} else if (condition.operator === 'neq') {
				return answer.answer !== condition.value;
			}
			return true;
		}
		t(path: string): string {
			return this.$t('components.simulator.' + path).toString();
		}
	}
</script>
